/**
 * The HTTP half of the protocol: accounts, user search, picture and video
 * uploads and push subscriptions.
 *
 * Everything else the client needs arrives over the socket. These are the few
 * calls that are genuinely request/response — they happen before a socket
 * exists (sign-in), answer a one-off question (who matches this prefix?), or
 * move bytes that have no business on a JSON stream (a pasted screenshot).
 */

import type { Attachment, User } from "@/chat/protocol"

export type AuthErrorCode =
  | "invalid_login"
  | "invalid_password"
  | "login_taken"
  | "invalid_credentials"
  | "rate_limited"
  | "unauthorized"
  | "unsupported_type"
  | "too_large"
  | "aborted"
  | "push_disabled"
  | "invalid_subscription"
  | "network"
  | "unknown"

/**
 * A failed request. `message` comes from the server where it sent one — those
 * are written to be shown to the user — and is a local fallback otherwise.
 */
export class AuthError extends Error {
  readonly code: AuthErrorCode
  readonly status: number

  constructor(code: AuthErrorCode, message: string, status = 0) {
    super(message)
    this.name = "AuthError"
    this.code = code
    this.status = status
  }
}

export type Session = { token: string; user: User }

/**
 * Where the API lives. Empty means same-origin, which is right both behind the
 * Vite dev proxy and in production, where the server serves the built client.
 */
export function resolveApiUrl(): string {
  return (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "")
}

const FALLBACK_MESSAGES: Record<AuthErrorCode, string> = {
  invalid_login: "Логин: 3–32 символа, только буквы, цифры и подчёркивание.",
  invalid_password: "Пароль должен быть от 8 до 128 символов.",
  login_taken: "Этот логин уже занят.",
  invalid_credentials: "Неверный логин или пароль.",
  rate_limited: "Слишком много попыток. Подождите немного.",
  unauthorized: "Сессия истекла. Войдите снова.",
  unsupported_type:
    "Можно отправлять только фото PNG, JPEG, GIF и WebP и видео MP4, MOV и WebM.",
  too_large: "Файл слишком большой.",
  aborted: "Загрузка отменена.",
  push_disabled: "Сервер не настроен на отправку уведомлений.",
  invalid_subscription: "Браузер вернул неполную подписку на уведомления.",
  network: "Не удалось связаться с сервером.",
  unknown: "Что-то пошло не так. Попробуйте ещё раз.",
}

function isAuthErrorCode(value: unknown): value is AuthErrorCode {
  return typeof value === "string" && value in FALLBACK_MESSAGES
}

async function request<T>(
  path: string,
  init: RequestInit & { token?: string | null } = {}
): Promise<T> {
  const { token, ...rest } = init
  const headers = new Headers(rest.headers)
  headers.set("Accept", "application/json")
  if (rest.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json")
  }
  if (token) headers.set("Authorization", `Bearer ${token}`)

  let response: Response
  try {
    response = await fetch(`${resolveApiUrl()}${path}`, { ...rest, headers })
  } catch {
    throw new AuthError("network", FALLBACK_MESSAGES.network)
  }

  let body: unknown = null
  try {
    body = await response.json()
  } catch {
    // A body-less or non-JSON reply; the status alone decides below.
  }

  if (response.ok) return body as T

  const error = (body ?? {}) as { error?: unknown; message?: unknown }
  const code = isAuthErrorCode(error.error)
    ? error.error
    : response.status === 401
      ? "unauthorized"
      : "unknown"
  const message =
    typeof error.message === "string" && error.message
      ? error.message
      : FALLBACK_MESSAGES[code]

  throw new AuthError(code, message, response.status)
}

export function register(login: string, password: string): Promise<Session> {
  return request<Session>("/api/auth/register", {
    method: "POST",
    body: JSON.stringify({ login, password }),
  })
}

export function login(login: string, password: string): Promise<Session> {
  return request<Session>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ login, password }),
  })
}

/**
 * Revokes the token everywhere. The server answers by closing every socket
 * that used it, so the caller should already have dropped its own.
 */
export async function logout(token: string): Promise<void> {
  await request<{ ok: true }>("/api/auth/logout", { method: "POST", token })
}

/** Validates a stored token on startup; throws `unauthorized` if it is dead. */
export async function me(token: string): Promise<User> {
  const { user } = await request<{ user: User }>("/api/auth/me", { token })
  return user
}

/** Users whose login starts with `q`, the caller excluded. Empty `q` is nobody. */
export async function searchUsers(token: string, q: string): Promise<User[]> {
  const prefix = q.trim()
  if (!prefix) return []

  const { users } = await request<{ users: User[] }>(
    `/api/users?q=${encodeURIComponent(prefix)}`,
    { token }
  )
  return users
}

export type UploadOptions = {
  /** Called with the share of the body sent so far, from 0 to 1. */
  onProgress?: (fraction: number) => void
  signal?: AbortSignal
}

/**
 * Uploads one picture or video and returns the attachment to reference from
 * `send`. The body is the raw bytes; the server sniffs the real format
 * itself, so the declared type is a courtesy rather than a claim it trusts.
 *
 * This is the one call made with XMLHttpRequest rather than `fetch`: a video
 * can take a minute to go up, and only XHR reports upload progress.
 */
export function uploadMedia(
  token: string,
  file: Blob,
  { onProgress, signal }: UploadOptions = {}
): Promise<Attachment> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new AuthError("aborted", FALLBACK_MESSAGES.aborted))
      return
    }

    const xhr = new XMLHttpRequest()
    xhr.open("POST", `${resolveApiUrl()}/api/uploads`)
    xhr.setRequestHeader("Accept", "application/json")
    xhr.setRequestHeader("Authorization", `Bearer ${token}`)
    xhr.setRequestHeader(
      "Content-Type",
      file.type || "application/octet-stream"
    )
    xhr.responseType = "json"

    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress(event.loaded / event.total)
      }
    }

    const onAbort = () => xhr.abort()
    signal?.addEventListener("abort", onAbort, { once: true })
    const settle = () => signal?.removeEventListener("abort", onAbort)

    xhr.onabort = () => {
      settle()
      reject(new AuthError("aborted", FALLBACK_MESSAGES.aborted))
    }
    xhr.onerror = () => {
      settle()
      reject(new AuthError("network", FALLBACK_MESSAGES.network))
    }
    xhr.onload = () => {
      settle()
      const body = (xhr.response ?? {}) as {
        attachment?: Attachment
        error?: unknown
        message?: unknown
      }
      if (xhr.status >= 200 && xhr.status < 300 && body.attachment) {
        resolve(body.attachment)
        return
      }

      const code = isAuthErrorCode(body.error)
        ? body.error
        : xhr.status === 401
          ? "unauthorized"
          : xhr.status === 413
            ? "too_large"
            : "unknown"
      const message =
        typeof body.message === "string" && body.message
          ? body.message
          : FALLBACK_MESSAGES[code]
      reject(new AuthError(code, message, xhr.status))
    }

    xhr.send(file)
  })
}

/** Absolute URL of an attachment, for `<img src>` and `<video src>`. */
export function attachmentSrc(attachment: Attachment): string {
  return `${resolveApiUrl()}${attachment.url}`
}

// --- Push -------------------------------------------------------------------

/** The VAPID public key to subscribe with; throws `push_disabled` if there is none. */
export async function pushKey(): Promise<string> {
  const { key } = await request<{ key: string }>("/api/push/key")
  return key
}

/** Registers (or re-registers, under this account) a browser's push subscription. */
export async function subscribePush(
  token: string,
  subscription: PushSubscriptionJSON
): Promise<void> {
  await request<{ ok: true }>("/api/push/subscriptions", {
    method: "POST",
    token,
    body: JSON.stringify(subscription),
  })
}

export async function unsubscribePush(
  token: string,
  endpoint: string
): Promise<void> {
  await request<{ ok: true }>("/api/push/subscriptions", {
    method: "DELETE",
    token,
    body: JSON.stringify({ endpoint }),
  })
}
